import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '@/hen-ui/utils';

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentProps<typeof TabsPrimitive.Root>
>(({ className, orientation, ...props }, ref) => (
  <TabsPrimitive.Root
    data-slot='tabs'
    data-orientation={orientation}
    className={cn(
      'group/tabs flex gap-6 data-[orientation=horizontal]:flex-col',
      className,
    )}
    ref={ref}
    {...props}
  />
));

Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentProps<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    data-slot='tabs-list'
    ref={ref}
    className={cn(
      // Base layout
      'inline-flex items-center justify-center w-fit',
      // Spacing
      'gap-8',
      // Orientation-specific
      'group-data-[orientation=horizontal]/tabs:h-8',
      'group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col',
      className,
    )}
    {...props}
  />
));

TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentProps<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    data-slot='tabs-trigger'
    ref={ref}
    className={cn(
      // Base layout
      'relative inline-flex flex-1 items-center justify-center whitespace-nowrap',
      // Spacing
      'gap-1 px-1.5',
      // Typography
      'text-sm font-bold',
      // Text Colors
      'text-neutral-900 hover:text-secondary-700 data-[state=active]:text-neutral-900 data-[state=active]:hover:text-secondary-700',
      // Transitions
      'transition-all',
      // Focus state
      'focus-visible:outline-neutral-200 focus-visible:outline-1 focus-visible:ring focus-visible:ring-neutral-100 focus-visible:rounded-md',
      // Disabled state
      'disabled:pointer-events-none disabled:opacity-50',

      // Active indicator (after pseudo-element)
      'after:bg-neutral-900 hover:after:bg-secondary-500 after:absolute after:opacity-0 after:transition-opacity',
      // Horizontal orientation indicator
      'group-data-[orientation=horizontal]/tabs:after:inset-x-0 group-data-[orientation=horizontal]/tabs:after:-bottom-1 group-data-[orientation=horizontal]/tabs:after:h-0.5',
      // Vertical orientation indicator
      'group-data-[orientation=vertical]/tabs:after:inset-y-0 group-data-[orientation=vertical]/tabs:after:-right-1 group-data-[orientation=vertical]/tabs:after:w-0.5',
      // Show indicator when active
      'data-[state=active]:after:opacity-100',

      // Vertical orientation adjustments
      'group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start',

      // Icon styling
      '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4',

      className,
    )}
    {...props}
  />
));

TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentProps<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    data-slot='tabs-content'
    ref={ref}
    className={cn('flex-1 text-sm outline-none', className)}
    {...props}
  />
));

TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
